import { Update } from '@ngrx/entity';
import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import { SafeAny } from '../../types/safe-any';
import { QuotePerson, QuoteStatus } from '../quotes/quotes.state.interface';

import {
  CustomerCodes,
  Note,
  OrderClient,
  SavedLists,
  SingleList,
  SingleListPayload,
  SingleListPUTApiResponse,
  singleOrderDetails,
} from './orders.model';

export const OrdersActions = createActionGroup({
  source: 'Orders/API',
  events: {
    'Load Orderss': props<{ orderss: singleOrderDetails[] }>(),
    'Add Orders': props<{ orders: singleOrderDetails }>(),
    'Upsert Orders': props<{ orders: singleOrderDetails }>(),
    'Add Orderss': props<{ orderss: singleOrderDetails[] }>(),
    'Upsert Orderss': props<{ orderss: singleOrderDetails[] }>(),
    'Update Orders': props<{ orders: Update<singleOrderDetails> }>(),
    'Update Orderss': props<{ orderss: Update<singleOrderDetails>[] }>(),
    'Delete Orders': props<{ id: string }>(),
    'Delete Orderss': props<{ ids: string[] }>(),
    'Clear Orderss': emptyProps(),
    'Load Order': props<{ id: string; isEdit?: boolean }>(),
    'Load Order Success': props<{ order: singleOrderDetails }>(),
    'Load Order Failure': props<{ error: any }>(),
    // Order Assignees
    'Load Order Assignees': props<{ payload: any }>(),
    'Load Order Assignees Success': props<{ assignees: QuotePerson[] }>(),
    'Load Order Assignees Failure': props<{ error: string }>(),
    // Order Statuses
    'Load Order Statuses': props<{ payload: any }>(),
    'Load Order Statuses Success': props<{ statuses: QuoteStatus[] }>(),
    'Load Order Statuses Failure': props<{ error: string }>(),
    // Order Clients
    'Load Order Clients': props<{ payload?: any }>(),
    'Load Order Clients Success': props<{ clients: OrderClient[] }>(),
    'Load Order Clients Failure': props<{ error: string }>(),
    // Notes
    'Add Order Note': props<{ orderId: number; note: string }>(),
    'Add Order Note Success': props<{ orderId: number; noteObj: Note }>(),
    'Add Order Note Failure': props<{ orderId: number; error: any }>(),
    // Archive Order
    'Archive Order': props<{ orderId: string; isArchived: boolean }>(),
    'Archive Order Success': props<{ orderId: string; isArchived: boolean }>(),
    'Archive Order Failure': props<{ error: string }>(),
    // Delete Order
    'Delete Order': props<{ orderId: string }>(),
    'Delete Order Success': props<{ orderId: string }>(),
    'Delete Order Failure': props<{ error: string }>(),
    // Order Materials Categories
    'Load Order Materials Categories': props<{ payload: { identifier: string } }>(),
    'Load Order Materials Categories Success': props<{ categories: QuoteStatus[] }>(),
    'Load Order Materials Categories Failure': props<{ error: string }>(),
    // Order Materials Status
    'Load Order Materials Status': props<{ payload: { identifier: string } }>(),
    'Load Order Materials Status Success': props<{ statuses: QuoteStatus[] }>(),
    'Load Order Materials Status Failure': props<{ error: string }>(),
    // Packing Slip Categories
    'Load Packing Slip Categories': props<{ payload: { identifier: string } }>(),
    'Load Packing Slip Categories Success': props<{ categories: QuoteStatus[] }>(),
    'Load Packing Slip Categories Failure': props<{ error: string }>(),
    'Save Order': props<{ order: any; initiateRedirect: boolean }>(),
    'Save Order Success': props<{ order: any; initiateRedirect: boolean }>(),
    'Save Order Failure': props<{ error: any }>(),
    // Load Orders List (for table)
    'Load Orders List': props<{ payload: any }>(),
    'Load Orders List Success': props<{
      orders: singleOrderDetails[];
      totalCount: number;
      priceTotal: number;
      priceTotalNotShipped: number;
    }>(),
    'Load Orders List Failure': props<{ error: string }>(),
    // Load My Orders
    'Load My Orders': props<{ payload: SafeAny }>(),
    'Load My Orders Success': props<{
      orders: singleOrderDetails[];
      totalCount: number;
      totalPrice: number;
    }>(),
    'Load My Orders Failure': props<{ error: string }>(),
    // Load Past Due Orders
    'Load Past Due Orders': props<{ payload: SafeAny }>(),
    'Load Past Due Orders Success': props<{
      orders: singleOrderDetails[];
      totalCount: number;
      totalPrice: number;
    }>(),
    'Load Past Due Orders Failure': props<{ error: string }>(),
    // Load Orders Due Today
    'Load Orders Due Today': props<{ payload: SafeAny }>(),
    'Load Orders Due Today Success': props<{
      orders: singleOrderDetails[];
      totalCount: number;
      totalPrice: number;
    }>(),
    'Load Orders Due Today Failure': props<{ error: string }>(),
    // Create Order
    'Create Order': props<{ payload: SafeAny }>(),
    'Create Order Success': props<{ order: singleOrderDetails }>(),
    'Create Order Failure': props<{ error: string }>(),
    // Lists
    'Load Lists': props<{
      payload: {
        offset: number;
        limit: number;
        sort?: string;
      };
    }>(),
    'Load Lists Success': props<{
      payload: {
        lists: SavedLists[];
        totalCount: number;
      };
    }>(),
    'Load Lists Failure': props<{ error: string }>(),
    // Customer Codes
    'Load Customer Codes': props<{ payload?: any }>(),
    'Load Customer Codes Success': props<{ customerCodes: CustomerCodes }>(),
    'Load Customer Codes Failure': props<{ error: string }>(),
    // Single List
    'Load Single List': props<{ id: number; payload?: any }>(),
    'Load Single List Success': props<{ singleList: SingleList }>(),
    'Load Single List Failure': props<{ error: string }>(),
    // Create Single List
    'Create Single List': props<{ payload: SingleListPayload }>(),
    'Create Single List Success': props<{ response: SingleListPUTApiResponse }>(),
    'Create Single List Failure': props<{ error: string }>(),
    // Update Single List
    'Update Single List': props<{ id: number; payload: SingleListPayload }>(),
    'Update Single List Success': props<{ response: SingleListPUTApiResponse }>(),
    'Update Single List Failure': props<{ error: string }>(),
    // Delete List
    'Delete List': props<{ id: number }>(),
    'Delete List Success': props<{ id: number }>(),
    'Delete List Failure': props<{ error: string }>(),
    // Export List
    'Export List': props<{ id: number; exportFormat: 'csv' | 'pdf' }>(),
    'Export List Success': props<{ id: number; exportFormat: 'csv' | 'pdf' }>(),
    'Export List Failure': props<{ error: string }>(),
    // Load Export Details
    'Load Export Details': props<{ orderId: string }>(),
    'Load Export Details Success': props<{
      exportDetails: import('./orders.model').OrderExportDetails;
    }>(),
    'Load Export Details Failure': props<{ error: any }>(),
    // Export Order
    'Export Order': props<{
      orderId: number;
      payload: import('./orders.model').OrderExportPayload;
    }>(),
    'Export Order Success': props<{ response: any }>(),
    'Export Order Failure': props<{ error: any }>(),
    // Export Orders Bundle
    'Export Orders Bundle': props<{
      payload: import('./orders.model').ExportOrdersBundlePayload;
    }>(),
    'Export Orders Bundle Success': props<{
      response: import('./orders.model').ExportOrdersBundleResponse;
    }>(),
    'Export Orders Bundle Failure': props<{ error: any }>(),
    // Set Unlock
    'Set Unlock': props<{ id: string }>(),
    'Set Unlock Success': props<{ id: string }>(),
    'Set Unlock Failure': props<{ id: string; error: string }>(),
  },
});

export const updateOrderInStore = createAction(
  '[Orders/Store] Update Order In Store',
  props<{ order: singleOrderDetails }>(),
);

export const loadExportDetails = createAction(
  '[Orders/API] Load Export Details',
  props<{ orderId: number }>(),
);

export const loadExportDetailsSuccess = createAction(
  '[Orders/API] Load Export Details Success',
  props<{ exportDetails: import('./orders.model').OrderExportDetails }>(),
);

export const loadExportDetailsFailure = createAction(
  '[Orders/API] Load Export Details Failure',
  props<{ error: any }>(),
);

export const exportOrder = createAction(
  '[Orders/API] Export Order',
  props<{ orderId: number; payload: import('./orders.model').OrderExportPayload }>(),
);

export const exportOrderSuccess = createAction(
  '[Orders/API] Export Order Success',
  props<{ response: any }>(),
);

export const exportOrderFailure = createAction(
  '[Orders/API] Export Order Failure',
  props<{ error: any }>(),
);

export const exportOrdersBundle = createAction(
  '[Orders/API] Export Orders Bundle',
  props<{ payload: import('./orders.model').ExportOrdersBundlePayload }>(),
);

export const exportOrdersBundleSuccess = createAction(
  '[Orders/API] Export Orders Bundle Success',
  props<{ response: import('./orders.model').ExportOrdersBundleResponse }>(),
);

export const exportOrdersBundleFailure = createAction(
  '[Orders/API] Export Orders Bundle Failure',
  props<{ error: any }>(),
);
